/**
 * BreakableTilemap (+ BreakableTilemapProxy): dashing (or moving faster than the dash speed) into a
 * breakable tile removes it, spawns the SpriteExploder prefab and rebuilds the tilemap collider.
 * Broken tiles live in the tilemap's tiles array and collider shape, both part of the world snapshot,
 * so checkpoint restores bring them back.
 */
import { GameBehaviour } from '../game.ts';
import type { Collision2D, SimCollider, SimGo } from '../world.ts';
import { list } from '../props.ts';
import { invert, mul, compose } from '../../../model/xform.ts';
import { tileLoopsAfterRemoval, type Loop } from '../tileCollision.ts';
import { playerOf } from './util.ts';
import { SpriteExploder } from './effects.ts';
import type { V2 } from '../unity.ts';

const cellKey = (x: number, y: number) => `${x},${y}`;

function angleDeg(a: V2, b: V2): number {
  const d = Math.sqrt((a.x * a.x + a.y * a.y) * (b.x * b.x + b.y * b.y));
  if (d < 1e-15) return 0;
  const c = Math.max(-1, Math.min(1, (a.x * b.x + a.y * b.y) / d));
  return (Math.acos(c) * 180) / Math.PI;
}

export class BreakableTilemapProxy extends GameBehaviour {
  static override checkpointable = false;
  handler: BreakableTilemap | null = null;
  collider: SimCollider | null = null;

  override onCollisionEnter(c: Collision2D) {
    this.handler?.handleCollision(this.go, this.collider!, c);
  }
  override onCollisionStay(c: Collision2D) {
    this.handler?.handleCollision(this.go, this.collider!, c);
  }
}

export class BreakableTilemap extends GameBehaviour {
  static override checkpointable = false;
  breakableTiles = new Set<string>();
  breakAllTheThings = false;
  breakableTilePrefab = '';
  /** tilemaps whose collider must be rebuilt before the next physics step */
  dirty = new Map<SimGo, { col: SimCollider; tiles: number[]; loops: Loop[] }>();
  private index = new WeakMap<number[], Map<string, number>>();
  override noSave = ['breakableTiles', 'dirty', 'index'];

  override awake() {
    const d = this.data;
    for (const t of list(d.breakableTiles)) if (t?.guid) this.breakableTiles.add(String(t.guid));
    this.breakAllTheThings = d.breakAllTheThings === '1' || d.breakAllTheThings === true;
    this.breakableTilePrefab = d.breakableTilePrefab?.guid ? String(d.breakableTilePrefab.guid) : '';
  }

  override start() {
    for (const g of this.game.gos) {
      if (!g.tilemap || g.destroyed) continue;
      const col = g.colliders.find((c) => c.shape.kind === 'chains');
      if (!col || !g.tilemap.tiles.some((_, i) => i % 4 === 2 && this.canBreakTile(g, g.tilemap!.tiles[i]))) continue;
      const proxy = new BreakableTilemapProxy(g, {});
      proxy.unityType = 'BreakableTilemapProxy';
      proxy.handler = this;
      proxy.collider = col;
      this.game.addBehaviour(proxy);
    }
  }

  private canBreakTile(tm: SimGo, p: number): boolean {
    if (p < 0) return false;
    if (this.breakAllTheThings) return true;
    const guid = tm.tilemap!.palette[p]?.tile;
    return !!guid && this.breakableTiles.has(guid);
  }

  /** cell -> index into the tiles array (quads of x, y, palette, flags) */
  private cells(tiles: number[]): Map<string, number> {
    let m = this.index.get(tiles);
    if (!m) {
      m = new Map();
      for (let i = 0; i < tiles.length; i += 4) if (tiles[i + 2] >= 0) m.set(cellKey(tiles[i], tiles[i + 1]), i);
      this.index.set(tiles, m);
    }
    return m;
  }

  handleCollision(tm: SimGo, col: SimCollider, c: Collision2D) {
    const player = playerOf(c.collider);
    if (!player || !tm.tilemap) return;
    const dashing = player.isDashing();
    if (!dashing) {
      const v = { ...player.lastPlayerVelocity };
      if (v.y < 0) v.y = 0;
      if (Math.hypot(v.x, v.y) <= player.dashVelocity) return;
    }
    const eps = 0.05;
    const compare = dashing ? player.dashDirection : player.lastPlayerVelocity;
    const points = new Map<string, V2>();
    for (const cp of c.contacts) {
      if (angleDeg(compare, cp.normal) >= 47) continue;
      const p = { x: cp.point.x + cp.normal.x * eps, y: cp.point.y + cp.normal.y * eps };
      points.set(`${p.x},${p.y}`, p);
    }
    if (!points.size) return;

    const tiles = tm.tilemap.tiles;
    const cells = this.cells(tiles);
    const inv = invert(tm.worldXform());
    const broken = new Set<number>();
    for (const p of points.values()) {
      const lx = inv.a * p.x + inv.c * p.y + inv.tx, ly = inv.b * p.x + inv.d * p.y + inv.ty;
      const cx = Math.floor(lx), cy = Math.floor(ly);
      const i = cells.get(cellKey(cx, cy));
      if (i === undefined || broken.has(i) || !this.canBreakTile(tm, tiles[i + 2])) continue;
      broken.add(i);
      const w = mul(tm.worldXform(), compose(cx + 0.5, cy + 0.5, 0));
      const ex = this.breakableTilePrefab ? this.game.instantiate(this.breakableTilePrefab, { x: w.tx, y: w.ty }, this.go.rotation) : null;
      this.game.levelAction('breakable', player);
      const se = ex?.getBehaviour(SpriteExploder);
      if (se) {
        const e = tm.tilemap.palette[tiles[i + 2]];
        se.sourceSprite = e ? this.game.gd.spriteByName.get(`${e.tex}|${e.sprite}`) ?? null : null;
        se.sourceColor = [...tm.tilemap.color];
        se.breakPos = player.go.position;
      }
    }
    if (!broken.size) return;
    this.sound('breakTile', [...points.values()][0]);
    if (!dashing) player.body.velocity = player.lastPlayerVelocity;
    const next: number[] = [];
    const removed: [number, number][] = [];
    for (let i = 0; i < tiles.length; i += 4) {
      if (broken.has(i)) removed.push([tiles[i], tiles[i + 1]]);
      else next.push(tiles[i], tiles[i + 1], tiles[i + 2], tiles[i + 3]);
    }
    tm.tilemap.tiles = next;
    if (!this.game.pixels) return;
    const loops = tileLoopsAfterRemoval(tiles, next, removed, { palette: tm.tilemap.palette, gd: this.game.gd, pixels: this.game.pixels });
    this.dirty.set(tm, { col, tiles: next, loops });
  }

  /** TilemapCollider2D.ProcessTilemapChanges: the new shape goes in before the next physics step */
  override fixedUpdate() {
    if (!this.dirty.size) return;
    for (const [tm, d] of this.dirty) if (tm.tilemap?.tiles === d.tiles) d.col.setShape({ kind: 'chains', loops: d.loops });
    this.dirty.clear();
  }
}
